import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { ArticleStructuredData, BreadcrumbStructuredData } from '../components/StructuredData';
import ComparisonTable from './ComparisonTable';
import { featureAreas } from './features';
import { getComparison, getCompetitor } from './registry';
import NotFoundPage from '../NotFoundPage';

const SITE_URL = 'https://www.greyfielddata.com';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

function formatDate(iso: string) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function isExternal(url: string) {
  return /^https?:\/\//.test(url);
}

const LINK = /\[([^\]]+)\]\(([^)]+)\)/g;

/** Paragraph text may contain [label](/internal-path) links. */
function renderInline(text: string) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const [whole, label, href] = match;
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    parts.push(
      isExternal(href)
        ? <a key={index} href={href} target="_blank" rel="noopener noreferrer nofollow">{label}</a>
        : <Link key={index} to={href}>{label}</Link>
    );
    last = index + whole.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function sentence(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function plainText(text: string) {
  return text.replace(LINK, '$1');
}

export default function ComparePage() {
  const { slug } = useParams<{ slug: string }>();
  const page = slug ? getComparison(slug) : undefined;
  if (!page) return <NotFoundPage />;

  const ours = getCompetitor(page.ours);
  const theirs = getCompetitor(page.theirs);
  const url = `${SITE_URL}/compare/${page.slug}`;
  const related = page.related.map((relatedSlug) => getComparison(relatedSlug)).filter((item) => item !== undefined);
  const nextStep = page.group === 'bi'
    ? { title: 'Weighing Sigma for your team?', body: 'Bring one report your team relies on. We can look at the data, the calculations, and how people use it, and tell you whether Sigma is a good fit.', href: '/services/sigma-consulting', label: 'Explore Sigma consulting' }
    : { title: 'Not sure a packaged tool covers it?', body: 'Bring the questions your current reports cannot answer. We will tell you whether a packaged tool, a warehouse, or both is the better fit.', href: '/ecommerce', label: 'Explore ecommerce data warehouses' };

  const faqData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: plainText(item.answer) },
    })),
  };

  const oursLabel = ours.shortName ?? ours.name;
  const oursTitle = sentence(oursLabel);
  const pickName = (pick: 'ours' | 'theirs' | 'both') => (pick === 'ours' ? oursTitle : pick === 'theirs' ? theirs.name : 'Both');
  const toc = [
    { id: 'short-answer', label: 'The short answer' },
    { id: 'how-different', label: `How ${theirs.name} and ${oursLabel} differ` },
    { id: 'at-a-glance', label: 'Pricing at a glance' },
    { id: 'feature-table', label: 'Feature comparison' },
    { id: 'in-detail', label: 'In more detail' },
    { id: 'where-they-win', label: `Where ${theirs.name} is better` },
    { id: 'who-should-pick', label: 'Who should pick which' },
    ...(page.byTeam.length > 0 ? [{ id: 'by-team', label: 'Recommendations by team' }] : []),
    ...(page.faq.length > 0 ? [{ id: 'faq', label: 'Questions people ask' }] : []),
    { id: 'sources', label: 'Sources and corrections' },
  ];

  return (
    <BlogLayout wide>
      <SEO
        title={page.metaTitle}
        description={page.description}
        type="article"
        url={url}
        publishedTime={page.published}
        modifiedTime={page.updated}
        keywords={page.keywords}
      />
      <ArticleStructuredData headline={page.title} description={page.description} url={url} datePublished={page.published} dateModified={page.updated} />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Comparisons', url: `${SITE_URL}/compare` },
          { name: page.title, url },
        ]}
      />
      {page.faq.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqData)}</script>
        </Helmet>
      )}

      <article className="mx-auto max-w-5xl">
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to="/compare" className="hover:text-slate-900">Comparisons</Link>
        </nav>

        <header className="border-b border-slate-200 pb-8">
          <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold tracking-wide text-[#1a4e94]">
            {page.group === 'bi' ? 'BI tool comparison' : 'Ecommerce reporting comparison'}
          </span>
          <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">{page.title}</h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-600">{page.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
            <span className="flex items-center gap-3">
              <img src="/images/linus-tse-linkedin-photo.jpeg" alt="" className="h-10 w-10 rounded-full object-cover" />
              <span>
                <Link to="/linus-tse-introduction" className="font-semibold text-slate-900 hover:text-blue-700">Linus Tse</Link>
                <span className="block">Greyfield Data</span>
              </span>
            </span>
            <span>Facts last checked <time dateTime={page.updated}>{formatDate(page.updated)}</time></span>
          </div>
        </header>

        <details className="mt-8 border-b border-slate-200 pb-5">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700 hover:text-blue-700">
            Contents <span className="ml-2 font-normal text-slate-500">{toc.length} sections</span>
          </summary>
          <nav aria-label="Table of contents" className="pt-4">
            <ol className="grid list-decimal gap-x-8 gap-y-2 pl-5 sm:grid-cols-2 marker:text-slate-400">
              {toc.map((item) => (
                <li key={item.id} className="pl-1"><a href={`#${item.id}`} className="block py-1 text-sm text-slate-600 hover:text-blue-700">{item.label}</a></li>
              ))}
            </ol>
          </nav>
        </details>

        <section aria-labelledby="short-answer" className="mt-8 scroll-mt-28 rounded-2xl border border-blue-100 bg-[#f7faff] p-6 sm:p-8">
          <h2 id="short-answer" className="text-sm font-semibold uppercase tracking-wide text-[#1a4e94]">The short answer</h2>
          <p className="mt-3 text-lg leading-relaxed text-slate-800">{plainText(page.tldr)}</p>
        </section>

        <section aria-labelledby="how-different" className="mt-14">
          <h2 id="how-different" className="scroll-mt-28 text-2xl font-semibold text-slate-950">How {theirs.name} and {oursLabel} differ</h2>
          <ol className="mt-6 grid gap-5 md:grid-cols-3">
            {page.differences.map((item, index) => (
              <li key={item.heading} className="rounded-2xl border border-slate-200 p-6">
                <span className="text-sm font-semibold text-[#1a4e94]">0{index + 1}</span>
                <h3 className="mt-3 text-lg font-semibold leading-snug text-slate-950">{item.heading}</h3>
                <p className="mt-3 leading-relaxed text-slate-700 [&_a]:font-semibold [&_a]:text-[#1a4e94] [&_a]:underline">{renderInline(item.body)}</p>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="at-a-glance" className="mt-14">
          <h2 id="at-a-glance" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Pricing at a glance</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {[theirs, ours].map((product) => (
              <div key={product.id} className="rounded-2xl border border-slate-200 p-6">
                <h3 className="text-xl font-semibold text-slate-950">{product.name}</h3>
                {product.owner && <p className="mt-1 text-sm text-slate-500">{product.owner}</p>}
                <p className="mt-3 leading-relaxed text-slate-700">{product.summary}</p>
                <dl className="mt-5 space-y-3 text-sm">
                  <div><dt className="font-semibold text-slate-900">Pricing model</dt><dd className="text-slate-600">{sentence(product.pricing.model)}</dd></div>
                  <div><dt className="font-semibold text-slate-900">Published starting price</dt><dd className="text-slate-600">{sentence(product.pricing.publicStartingPrice)}</dd></div>
                  <div><dt className="font-semibold text-slate-900">Free tier</dt><dd className="text-slate-600">{sentence(product.pricing.freeTier)}</dd></div>
                  {product.pricing.notes && <div><dt className="font-semibold text-slate-900">What drives cost</dt><dd className="text-slate-600">{sentence(product.pricing.notes)}</dd></div>}
                </dl>
                <a
                  href={product.pricing.sourceUrl}
                  {...(isExternal(product.pricing.sourceUrl) ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
                  className="mt-4 inline-block text-xs text-slate-400 underline underline-offset-2 hover:text-[#1a4e94]"
                >
                  Pricing source, checked {formatDate(product.pricing.checkedOn)}
                </a>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="feature-table" className="mt-14">
          <h2 id="feature-table" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Feature comparison</h2>
          <p className="mt-2 max-w-3xl text-slate-600">"Not verified" means we could not confirm it from public docs, not that the feature is missing. Each cell links to the page we checked.</p>
          {featureAreas(page.group).map((group) => (
            <div key={group.area} className="mt-8">
              <h3 className="mb-3 text-lg font-semibold text-slate-900">{group.area}</h3>
              <ComparisonTable ours={ours} theirs={theirs} features={group.features} caption={group.area} />
            </div>
          ))}
        </section>

        <aside aria-label="Talk it through" className="mt-10 flex flex-col gap-4 rounded-2xl bg-[#0a1628] p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div>
            <p className="text-lg font-semibold">{nextStep.title}</p>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-300">A 30-minute call, no deck. We will tell you if the other option fits better.</p>
          </div>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 justify-center rounded-xl bg-white px-5 py-3 font-semibold text-[#0a1628] hover:bg-slate-100">Book a free consultation</a>
        </aside>

        <div id="in-detail" className="md-article mt-4 scroll-mt-28">
          {page.sections.map((section) => (
            <section key={section.heading} className="[&>p+p]:mt-5">
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph}>{renderInline(paragraph)}</p>)}
            </section>
          ))}
        </div>

        <section aria-labelledby="where-they-win" className="mt-14">
          <h2 id="where-they-win" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Where {theirs.name} is better</h2>
          <ul className="mt-5 space-y-3">
            {theirs.strengths.map((item) => (
              <li key={item} className="flex gap-3 leading-relaxed text-slate-700"><span aria-hidden="true" className="font-semibold text-emerald-700">+</span>{item}</li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="who-should-pick" className="mt-14">
          <h2 id="who-should-pick" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Who should pick which</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-950">Pick {theirs.name} if</h3>
              <ul className="mt-4 list-disc space-y-3 pl-5 marker:text-slate-400">
                {page.pickThem.map((item) => <li key={item} className="leading-relaxed text-slate-700">{item}</li>)}
              </ul>
            </div>
            <div className="rounded-2xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-950">Pick {oursLabel} if</h3>
              <ul className="mt-4 list-disc space-y-3 pl-5 marker:text-slate-400">
                {page.pickUs.map((item) => <li key={item} className="leading-relaxed text-slate-700">{item}</li>)}
              </ul>
            </div>
          </div>
        </section>

        {page.byTeam.length > 0 && (
          <section aria-labelledby="by-team" className="mt-14">
            <h2 id="by-team" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Recommendations by team</h2>
            <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full min-w-[560px] text-left">
                <thead className="bg-slate-50 text-sm text-slate-500">
                  <tr><th scope="col" className="px-4 py-3 font-semibold">If you are</th><th scope="col" className="px-4 py-3 font-semibold">Pick</th><th scope="col" className="px-4 py-3 font-semibold">Why</th></tr>
                </thead>
                <tbody>
                  {page.byTeam.map((row) => (
                    <tr key={row.team} className="border-t border-slate-200 align-top">
                      <th scope="row" className="px-4 py-3 font-semibold text-slate-900">{row.team}</th>
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-[#1a4e94]">{pickName(row.pick)}</td>
                      <td className="px-4 py-3 leading-relaxed text-slate-700">{row.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {theirs.recentChanges.length > 0 && (
          <section aria-labelledby="recent-changes" className="mt-14">
            <h2 id="recent-changes" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Recent changes to {theirs.name}</h2>
            <ul className="mt-5 space-y-3">
              {theirs.recentChanges.map((change) => (
                <li key={change.change} className="leading-relaxed text-slate-700">
                  <span className="font-semibold text-slate-900">{change.date}</span> {change.change}
                  {change.sourceUrl && (
                    <a href={change.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="ml-2 text-xs text-slate-400 underline underline-offset-2 hover:text-[#1a4e94]">source</a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {page.faq.length > 0 && (
          <section aria-labelledby="faq" className="mt-14">
            <h2 id="faq" className="scroll-mt-28 text-2xl font-semibold text-slate-950">Questions people ask</h2>
            <div className="mt-6 space-y-8">
              {page.faq.map((item) => (
                <div key={item.question}>
                  <h3 className="text-lg font-semibold text-slate-950">{item.question}</h3>
                  <p className="mt-2 leading-relaxed text-slate-700 [&_a]:font-semibold [&_a]:text-[#1a4e94] [&_a]:underline">{renderInline(item.answer)}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section aria-labelledby="sources" className="mt-14 rounded-2xl bg-slate-50 p-6 sm:p-8">
          <h2 id="sources" className="scroll-mt-28 text-xl font-semibold text-slate-950">Sources and corrections</h2>
          <p className="mt-3 max-w-3xl leading-relaxed text-slate-600">
            We checked these pages on the dates shown. Products change quickly. If you work at {theirs.name} and something here is wrong or out of date, email <a href="mailto:linus@greyfielddata.com" className="font-semibold text-[#1a4e94] underline underline-offset-2">linus@greyfielddata.com</a> and we will fix it.
          </p>
          <ul className="mt-5 grid gap-2 text-sm sm:grid-cols-2">
            {[...theirs.sources, ...ours.sources].map((source) => (
              <li key={`${source.url}-${source.title}`}>
                <a
                  href={source.url}
                  {...(isExternal(source.url) ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
                  className="text-slate-600 underline underline-offset-2 hover:text-[#1a4e94]"
                >
                  {source.title}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <aside aria-label="Get help choosing" className="mt-14 rounded-2xl border border-blue-100 bg-slate-50 p-6 sm:p-8">
          <h2 className="text-2xl font-semibold text-slate-950">{nextStep.title}</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-slate-600">{nextStep.body}</p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-xl bg-[#1a4e94] px-5 py-3 font-semibold text-white hover:bg-blue-800">Book a free consultation</a>
            <Link to={nextStep.href} className="font-semibold text-[#1a4e94] underline underline-offset-4">{nextStep.label}</Link>
          </div>
        </aside>

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="mb-6 text-2xl font-bold text-gray-900">Related comparisons</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {related.map((item) => (
                <Link key={item.slug} to={`/compare/${item.slug}`} className="group rounded-2xl border border-gray-200 bg-white p-6 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg">
                  <h3 className="text-xl font-semibold leading-snug text-gray-900 group-hover:text-[#1a4e94]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.description}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="mt-12">
          <Link to="/compare" className="text-blue-600 hover:text-blue-800">← All comparisons</Link>
        </p>
      </article>
    </BlogLayout>
  );
}
